import CloseIcon from '@material-symbols/svg-300/rounded/close.svg'
import NavigateBeforeIcon from '@material-symbols/svg-300/rounded/navigate_before.svg'
import NavigateNextIcon from '@material-symbols/svg-300/rounded/navigate_next.svg'
import DownloadIcon from '@material-symbols/svg-300/rounded/download.svg'

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import Button from '../../components/basics/Button/Button'
import Menu from '../../components/basics/Menu/Menu'
import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import { Asset } from '../../models/Asset'
import { useLearningItem } from '../../state/entities'
import useAssets from '../../state/queries/useAssets'
import useRoutes from '../../state/useRoutes'
import * as api from '../../state/api'

import styles from './CourseAssetPage.module.scss'

/**********************************************************************
 * CourseAssets
 **********************************************************************/
type CourseAssetsProps = {
  learningItem: AnonymousLearningItem
  assets: Asset[]
  currentAssetUuid: string
  onClose?: () => void
  onChangeAsset?: (assetUuid: string) => void
}

const CourseAssets = React.memo(function CourseAssets({
  assets,
  currentAssetUuid,
  onClose,
  onChangeAsset,
}: CourseAssetsProps) {
  const { t } = useTranslation()

  const currentAssetIndex = assets.findIndex(({ uuid }) => uuid === currentAssetUuid)
  if (currentAssetIndex === -1) {
    throw new Error('Not found')
  }
  const currentAsset = assets[currentAssetIndex]

  const assetUrl = api.url(`course/${currentAsset.learningItemUuid}/asset/${currentAsset.uuid}`)

  const assetElement = useMemo(() => {
    if (currentAsset.mimeType.startsWith('video')) {
      return (
        <video
          key={currentAsset.uuid}
          controls
          src={assetUrl}
          controlsList={currentAsset.downloadable ? '' : 'nodownload'}
          onContextMenu={currentAsset.downloadable ? () => {} : (e) => e.preventDefault()}
        />
      )
    }
    if (currentAsset.mimeType.startsWith('audio')) {
      return <audio key={currentAsset.uuid} controls src={assetUrl} />
    }
    if (currentAsset.mimeType.startsWith('image')) {
      return <object key={currentAsset.uuid} type={currentAsset.mimeType} data={assetUrl} />
    }
    if (currentAsset.mimeType.startsWith('application/pdf')) {
      return <object key={currentAsset.uuid} type={currentAsset.mimeType} data={assetUrl} />
    }

    return (
      <div className={styles.downloadLink}>
        <p>
          {currentAsset.filename}{' '}
          {typeof currentAsset.byteSize === 'number'
            ? `(${t('format.BYTES', { bytes: currentAsset.byteSize })})`
            : null}
        </p>
        <Button filled component="a" href={assetUrl} download={currentAsset.filename}>
          <DownloadIcon />
          {t('asset.cta download')}
        </Button>
        {Boolean(onClose) && (
          <Button tiny className={styles.navigationButton} onClick={onClose}>
            <CloseIcon />
          </Button>
        )}
      </div>
    )
  }, [currentAsset, onClose])

  const handleGotoAsset = useCallback<React.MouseEventHandler<HTMLElement>>(
    (e) => {
      const targetAssetUuid = e.currentTarget.dataset.targetAssetUuid
      if (onChangeAsset && targetAssetUuid) {
        onChangeAsset(targetAssetUuid)
      }
    },
    [assets, onChangeAsset]
  )

  const previousAssetUuid = useMemo(
    () => assets[(currentAssetIndex - 1 + assets.length) % assets.length]?.uuid,
    [currentAssetIndex, assets]
  )
  const nextAssetUuid = useMemo(
    () => assets[(currentAssetIndex + 1 + assets.length) % assets.length]?.uuid,
    [currentAssetIndex, assets]
  )

  return (
    <>
      <div className={styles.infoBar}>
        <Menu small label={currentAsset.filename} className={styles.assetSelectionMenu}>
          {assets.map((asset) => (
            <li key={asset.uuid}>
              <Button menuItem data-target-asset-uuid={asset.uuid} onClick={handleGotoAsset}>
                {asset.filename}
              </Button>
            </li>
          ))}
        </Menu>
        <div className={styles.assetDescription}>{currentAsset.description}</div>
        {Boolean(onChangeAsset && assets.length > 1) && (
          <>
            {Boolean(previousAssetUuid) && (
              <Button
                small
                className={styles.navigationButton}
                data-target-asset-uuid={previousAssetUuid}
                onClick={handleGotoAsset}
              >
                <NavigateBeforeIcon />
              </Button>
            )}
            {Boolean(nextAssetUuid) && (
              <Button
                small
                className={styles.navigationButton}
                data-target-asset-uuid={nextAssetUuid}
                onClick={handleGotoAsset}
              >
                <NavigateNextIcon />
              </Button>
            )}
          </>
        )}
        {currentAsset.downloadable && (
          <Button small component="a" href={assetUrl} download={currentAsset.filename}>
            <DownloadIcon />
          </Button>
        )}
        {Boolean(onClose) && (
          <Button small filled className={styles.navigationButton} onClick={onClose}>
            <CloseIcon />
          </Button>
        )}
      </div>
      <div className={styles.assetContainer}>{assetElement}</div>
    </>
  )
})

/**********************************************************************
 * CourseAssetPage
 **********************************************************************/
export default React.memo(function CourseAssetPage() {
  const routes = useRoutes()

  const { uuid, assetUuid } = useParams()

  const { learningItem } = useLearningItem(uuid)
  const { assets } = useAssets(uuid!)

  const handleClose = useCallback(() => routes.course.goto({ uuid: uuid! }), [routes, uuid])

  const handleChangeAsset = useCallback(
    (assetUuid: string) => routes.courseAsset.goto({ uuid: uuid!, assetUuid }),
    [routes, uuid]
  )

  if (learningItem.isDummy || learningItem.error || assets.length === 0) {
    return
  }

  return (
    <CourseAssets
      learningItem={learningItem}
      assets={assets}
      currentAssetUuid={assetUuid!}
      onClose={handleClose}
      onChangeAsset={handleChangeAsset}
    />
  )
})

/**********************************************************************
 * CourseAssetPageBreadcrumb
 **********************************************************************/
export function CourseAssetPageBreadcrumb() {
  const { uuid } = useParams()
  const { learningItem } = useLearningItem(uuid)
  const routes = useRoutes()
  return <Link to={routes.course.url({ uuid: uuid! })}>{learningItem.title}</Link>
}
